import { FC, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { startCase } from "lodash";
import { usePostHog } from "posthog-js/react";
import { useHistory } from "react-router-dom";

import { ConnectAccountErrorMessage } from "components";
import { CredentialsForm } from "containers";
import * as addRoyaltyAccountRoutes from "containers/AddRoyaltyAccount/routes";
import { FormValues } from "containers/CredentialsForm";
import { handleConnectAccount } from "services/royaltyAccount";
import { ErrorInfo } from "services/royaltyAccount/types";
import { useValidateRoyaltyAccountCredentials } from "services/validateRoyaltyAccountCredentialsTasks/validateCredentials";
import { getOrCreateValuation } from "services/valuation";

import { BaseConnectAccountProps } from "../..";
import { SubaccountOption } from "../SubaccountSelection";
import { SubaccountSelection, TimeForEstimation } from "../";

const processSubaccounts = (data: Record<string, string>): SubaccountOption[] =>
  Object.entries(data).map(([subaccountId, subaccountName]) => ({
    value: subaccountId,
    display: subaccountName,
  }));

const ConnectWithSubaccount: FC<BaseConnectAccountProps> = ({
  royaltyPayor,
}) => {
  const history = useHistory();
  const posthog = usePostHog();
  const [verifiedAccount, setVerifiedAccount] = useState<FormValues | null>(
    null,
  );
  const {
    isOnTwoFAStep,
    validateCredentials,
    validateCredentialsErrorInfo,
    validationResult,
  } = useValidateRoyaltyAccountCredentials();
  const [
    connectionErrorInfo,
    setConnectionErrorInfo,
  ] = useState<ErrorInfo | null>(null);

  const errorInfo = validateCredentialsErrorInfo || connectionErrorInfo;

  const usernameOrEmailDisplay =
    royaltyPayor.username_or_email === "username"
      ? "username"
      : "email address";

  const handleValidate = async (values: FormValues) => {
    setConnectionErrorInfo(null);
    const success = await validateCredentials({
      username: values.username,
      password: values.password,
      royalty_payor: royaltyPayor.name,
      two_fa_code: values.twoFACode || "",
    });
    if (success) {
      setVerifiedAccount(values);
    }
  };

  const handleConnect = async (subaccount: SubaccountOption) => {
    const asset = await handleConnectAccount(
      verifiedAccount,
      subaccount,
      royaltyPayor.name,
      setConnectionErrorInfo,
      posthog,
    );
    getOrCreateValuation(asset.id);
    history.push(
      addRoyaltyAccountRoutes.connectGetValuationRoyaltyPayorAccountSuccess(
        royaltyPayor.name,
      ),
    );
  };

  return !verifiedAccount ? (
    <>
      {!!errorInfo && (
        <Alert severity="warning" icon={false}>
          <ConnectAccountErrorMessage
            errorInfo={errorInfo}
            royaltyPayor={royaltyPayor}
          />
        </Alert>
      )}
      <Box mb={2}>
        <TimeForEstimation royaltyPayorName={royaltyPayor.display_name} />
      </Box>
      {isOnTwoFAStep ? (
        <Typography color="primary" align="left">
          Enter the Two-Factor Authentication code sent to you by{" "}
          {royaltyPayor.display_name}.
        </Typography>
      ) : (
        <>
          <Typography color="primary" gutterBottom align="left">
            Connect your {royaltyPayor.display_name} account by entering your{" "}
            {usernameOrEmailDisplay} and password exactly as you would on the{" "}
            {royaltyPayor.display_name} site.
          </Typography>
          <Typography color="primary" align="left">
            You’ll give our system temporary access to your{" "}
            {royaltyPayor.display_name} account where it can read statements and
            provide an analysis of your royalties.
          </Typography>
        </>
      )}
      <CredentialsForm
        royaltyPayor={royaltyPayor}
        onSubmit={handleValidate}
        isOnTwoFAStep={isOnTwoFAStep}
        usernameProps={{
          placeholder: `${royaltyPayor.display_name} ${startCase(
            usernameOrEmailDisplay,
          )}`,
        }}
        passwordProps={{ placeholder: `${royaltyPayor.display_name} Password` }}
        initialValues={{
          username: "",
          password: "",
          twoFACode: "",
        }}
      />
    </>
  ) : (
    <SubaccountSelection
      username={verifiedAccount.username}
      royaltyPayorName={royaltyPayor.display_name}
      subaccounts={processSubaccounts(validationResult || {})}
      onConnect={handleConnect}
    />
  );
};

export default ConnectWithSubaccount;
